import _ from "lodash";
import {
  GRADE_SCALE_TYPES_IN_ORDER,
  ROW_CHILD_ID_TO_GRADE_SCALE_TYPE,
  ROW_ITEM_KEYS,
  GRADING_PERIOD_COLUMNS_SECTION_ID,
  GRADE_SCALE_VIEW_TYPES,
} from "./constants";
import {
  getDefaultPeriodTemplate,
  filterGradeScaleConfig,
} from "../components/DisplaySettingsModal/modules/displaySettingsModalUtils";

export const TABS_CONTAINER_STYLE = {
  paddingBottom: "8px",
};

export const mapDisplayOrderToItems = ({
  itemDisplayOrder,
  itemConfigMap,
  metaLookup,
  buildItem,
  idPrefix,
}) =>
  _.map(itemDisplayOrder, itemId => {
    const itemConfig = _.get(itemConfigMap, itemId, {});
    const itemMeta = _.get(metaLookup, itemId, {});
    return buildItem({
      itemId,
      itemConfig,
      itemMeta,
      idPrefix,
    });
  });

export const getGradingPeriodWiseItems = ({
  selectedGradeMap,
  gradingPeriods: gradingPeriodsProp,
  sectionConfig,
  defaultGradeScalesConfig,
  orgGradeScalesData,
  curriculumProgramsData,
  metaLookup,
  buildItem,
}) => {
  const gradingPeriods = !_.isNil(gradingPeriodsProp)
    ? gradingPeriodsProp
    : _.filter(_.get(selectedGradeMap, "gradingPeriods", []), "isEnabled");

  // For new GPs: use Default columns/rows local gradeScales config
  const defaultPeriodTemplate = getDefaultPeriodTemplate(
    defaultGradeScalesConfig
  );

  return _.flatMap(gradingPeriods, ({ gradingPeriod }) => {
    const gradingPeriodId = _.get(gradingPeriod, "id");
    let gradingPeriodWiseConfig = _.get(
      sectionConfig,
      ["gradingPeriodWise", gradingPeriodId],
      _.get(sectionConfig, gradingPeriodId, {})
    );

    // If this period has no config (newly added), use Default columns/rows config
    // filtered by school to exclude grade scales from other schools
    if (
      (_.isEmpty(gradingPeriodWiseConfig) ||
        _.isEmpty(_.get(gradingPeriodWiseConfig, "displayOrder"))) &&
      defaultPeriodTemplate
    ) {
      gradingPeriodWiseConfig =
        filterGradeScaleConfig({
          periodConfig: defaultPeriodTemplate,
          selectedGradeMap,
          orgGradeScalesData,
          curriculumProgramsData,
        }) || defaultPeriodTemplate;
    }

    const gradingPeriodDisplayOrder = _.get(
      gradingPeriodWiseConfig,
      "displayOrder",
      []
    );
    const gradingPeriodConfigMap = _.get(gradingPeriodWiseConfig, "config", {});

    return mapDisplayOrderToItems({
      itemDisplayOrder: gradingPeriodDisplayOrder,
      itemConfigMap: gradingPeriodConfigMap,
      metaLookup,
      buildItem,
      idPrefix: gradingPeriodId,
    });
  });
};

export const getAcrossGradingPeriodsItems = ({
  sectionConfig,
  metaLookup,
  buildItem,
}) => {
  // Prefer explicit acrossGradingPeriods config
  const acrossConfig = _.get(sectionConfig, "acrossGradingPeriods");

  if (acrossConfig) {
    return mapDisplayOrderToItems({
      itemDisplayOrder: _.get(acrossConfig, "displayOrder", []),
      itemConfigMap: _.get(acrossConfig, "config", {}),
      metaLookup,
      buildItem,
    });
  }

  // Fallback: use the first gradingPeriodWise entry's config
  const gradingPeriodWise = _.get(sectionConfig, "gradingPeriodWise", {});
  const firstPeriodConfig = _.head(_.values(gradingPeriodWise));

  if (firstPeriodConfig) {
    return mapDisplayOrderToItems({
      itemDisplayOrder: _.get(firstPeriodConfig, "displayOrder", []),
      itemConfigMap: _.get(firstPeriodConfig, "config", {}),
      metaLookup,
      buildItem,
    });
  }

  // Final fallback: top-level config
  return mapDisplayOrderToItems({
    itemDisplayOrder: _.get(sectionConfig, "displayOrder", []),
    itemConfigMap: _.get(sectionConfig, "config", {}),
    metaLookup,
    buildItem,
  });
};

/**
 * Helper to merge config items into result map by type
 * @param {Object} configItems - Config items to merge
 * @param {Object} result - Result object to merge into
 */
const mergeConfigItemsByType = (configItems, result) => {
  _.forEach(configItems, (itemConfig, id) => {
    const type = _.get(itemConfig, "type", id);
    if (!type) return;

    if (!result[type]) {
      result[type] = {
        ids: [],
        label: _.get(itemConfig, "label", null),
      };
    }

    result[type].ids.push(id);
    if (!result[type].label) {
      result[type].label = _.get(itemConfig, "label", null);
    }
  });
};

export const getGradeScaleTypeMap = ({ transcriptConfiguration }) => {
  const gradeScales = _.get(transcriptConfiguration, "gradeScales", {});
  const viewType = _.get(gradeScales, "viewType");
  const result = {};

  // Merge top-level config as fallback (Default level has flat config)
  mergeConfigItemsByType(_.get(gradeScales, "config", {}), result);

  // For grades (has viewType), always use acrossGradingPeriods
  if (viewType) {
    mergeConfigItemsByType(
      _.get(gradeScales, "acrossGradingPeriods.config", {}),
      result
    );
    return result;
  }

  return result;
};

/**
 * Helper to merge labels from config items into label map
 * @param {Object} configItems - Config items to extract labels from
 * @param {Object} labelMap - Label map to merge into
 */
const mergeLabelsFromConfig = (configItems, labelMap) => {
  _.forEach(configItems, (itemConfig, id) => {
    const label = _.get(itemConfig, "label", null);
    if (label && !labelMap[id]) {
      labelMap[id] = label;
    }
  });
};

export const getGradeScaleLabelMap = ({ transcriptConfiguration }) => {
  const gradeScales = _.get(transcriptConfiguration, "gradeScales", {});
  const viewType = _.get(gradeScales, "viewType");
  const labelMap = {};

  // Merge labels from top-level config first (Default level has flat config)
  mergeLabelsFromConfig(_.get(gradeScales, "config", {}), labelMap);

  // For grades (has viewType), always use acrossGradingPeriods
  if (viewType) {
    mergeLabelsFromConfig(
      _.get(gradeScales, "acrossGradingPeriods.config", {}),
      labelMap
    );
    return labelMap;
  }

  return labelMap;
};

export const getAvailableChildTypes = ({ gradeScaleTypeMap }) =>
  _.filter(
    GRADE_SCALE_TYPES_IN_ORDER,
    type => !_.isEmpty(_.get(gradeScaleTypeMap, [type, "ids"], []))
  );

const ROW_ITEM_TO_DEFAULT_CHILD_TYPES = {
  [ROW_ITEM_KEYS.TOTAL]: ["FINAL_SCORE", "FINAL_GRADE", "CREDIT"],
  [ROW_ITEM_KEYS.AVERAGE]: ["FINAL_SCORE", "FINAL_GRADE", "CREDIT"],
  [ROW_ITEM_KEYS.GPA]: [],
};

export const getFallbackChildTypes = ({
  itemId,
  rowItem,
  enabledGradeScaleIds,
  gradeScaleTypeMap,
}) => {
  const rowItemChildren = _.get(rowItem, "children", []);
  const rowItemChildTypes = _.reduce(
    rowItemChildren,
    (acc, child) => {
      const childType = _.get(
        ROW_CHILD_ID_TO_GRADE_SCALE_TYPE,
        _.toLower(_.get(child, "id", ""))
      );
      if (childType) {
        acc.push(childType);
      }
      return acc;
    },
    []
  );

  if (!_.isEmpty(rowItemChildTypes)) {
    return _.uniq(rowItemChildTypes);
  }

  const typesFromEnabledIds = _.isEmpty(enabledGradeScaleIds)
    ? []
    : _.filter(GRADE_SCALE_TYPES_IN_ORDER, type => {
        const typeIds = _.get(gradeScaleTypeMap, [type, "ids"], []);
        return !_.isEmpty(_.intersection(typeIds, enabledGradeScaleIds));
      });

  if (!_.isEmpty(typesFromEnabledIds)) {
    return typesFromEnabledIds;
  }

  return _.get(ROW_ITEM_TO_DEFAULT_CHILD_TYPES, itemId, []);
};

export const getRowItemMetaById = ({ items, itemId }) =>
  _.find(items, item => _.toUpper(_.get(item, "id", "")) === itemId);

export const getParentCheckState = ({ children = [] }) => {
  const totalChildren = _.size(children);
  const enabledChildren = _.size(_.filter(children, child => child.isEnabled));

  if (enabledChildren === 0) {
    return { isEnabled: false, isIndeterminate: false };
  }

  if (enabledChildren === totalChildren) {
    return { isEnabled: true, isIndeterminate: false };
  }

  return { isEnabled: false, isIndeterminate: true };
};

export const buildRowChildren = ({
  childTypes,
  enabledGradeScaleIds,
  gradeScaleTypeMap,
  gradeScaleLabelMap,
  getResolvedLabel,
  itemId,
}) =>
  _.map(childTypes, type => {
    const typeInfo = _.get(gradeScaleTypeMap, type, {});
    const gradeScaleIds = _.get(typeInfo, "ids", []);

    const isEnabled = _.some(gradeScaleIds, id =>
      _.includes(enabledGradeScaleIds, id)
    );

    const enabledTypeIds = _.intersection(gradeScaleIds, enabledGradeScaleIds);
    const resolvedGradeScaleId =
      _.get(enabledTypeIds, "[0]") || _.get(gradeScaleIds, "[0]");
    const label = resolvedGradeScaleId
      ? _.get(gradeScaleLabelMap, resolvedGradeScaleId, null)
      : null;
    const resolvedLabel = getResolvedLabel(label) || "";

    return {
      id: `${itemId}_${type}`,
      type,
      gradeScaleIds,
      content: resolvedLabel,
      isEnabled,
      isCheckable: true,
    };
  });

export const getRowChildTypes = ({
  itemId,
  rowItem,
  enabledGradeScaleIds,
  gradeScaleTypeMap,
}) => {
  if (itemId === ROW_ITEM_KEYS.TOTAL || itemId === ROW_ITEM_KEYS.AVERAGE) {
    return _.get(ROW_ITEM_TO_DEFAULT_CHILD_TYPES, itemId, []);
  }

  const availableChildTypes = getAvailableChildTypes({ gradeScaleTypeMap });
  if (!_.isEmpty(availableChildTypes)) {
    return availableChildTypes;
  }

  return getFallbackChildTypes({
    itemId,
    rowItem,
    enabledGradeScaleIds,
    gradeScaleTypeMap,
  });
};

export const toggleRowChildItems = ({ currentItems, parentId, childId }) =>
  _.map(currentItems, item => {
    if (item.id !== parentId) return item;

    const updatedChildren = _.map(item.children || [], child =>
      child.id === childId ? { ...child, isEnabled: !child.isEnabled } : child
    );
    const { isEnabled, isIndeterminate } = getParentCheckState({
      children: updatedChildren,
    });

    return {
      ...item,
      children: updatedChildren,
      isEnabled,
      isIndeterminate,
    };
  });

export const toggleItemWithChildren = ({ item, isEnabled }) => {
  const hasChildren = !_.isEmpty(item.children);
  if (!hasChildren) {
    return { ...item, isEnabled, isIndeterminate: false };
  }

  return {
    ...item,
    isEnabled,
    isIndeterminate: false,
    children: _.map(item.children, child => ({
      ...child,
      isEnabled,
    })),
  };
};

export const buildListItemsMap = ({ sections = [] }) =>
  _.reduce(
    sections,
    (acc, section) => {
      acc[section.sectionId] = section.items;
      return acc;
    },
    {}
  );

export const getTermWiseColumnSections = ({
  section,
  sectionItems,
  gradingPeriods,
}) => {
  if (
    section.sectionId !== GRADING_PERIOD_COLUMNS_SECTION_ID ||
    _.isEmpty(gradingPeriods)
  ) {
    return [
      {
        ...section,
        listKey: section.sectionId,
        renderItems: sectionItems,
        gradingPeriodPrefix: null,
      },
    ];
  }

  const groupedSections = _.compact(
    _.map(gradingPeriods, ({ gradingPeriod }, index) => {
      const gradingPeriodId = _.get(gradingPeriod, "id", "");
      const gradingPeriodPrefix = `${gradingPeriodId}_`;
      const termItems = _.filter(sectionItems, item =>
        _.startsWith(_.get(item, "id", ""), gradingPeriodPrefix)
      );

      if (_.isEmpty(termItems)) {
        return null;
      }

      const gradingPeriodLabel =
        _.get(gradingPeriod, "label", "") ||
        _.get(gradingPeriod, "name", "") ||
        `Term ${index + 1}`;

      return {
        ...section,
        sectionId: `${section.sectionId}_${gradingPeriodId}`,
        title: gradingPeriodLabel,
        listKey: section.sectionId,
        renderItems: termItems,
        gradingPeriodPrefix,
        showSettings: section.showSettings,
        showManageColumns: section.showManageColumns,
      };
    })
  );

  if (_.isEmpty(groupedSections)) {
    return [
      {
        ...section,
        listKey: section.sectionId,
        renderItems: sectionItems,
        gradingPeriodPrefix: null,
      },
    ];
  }

  return groupedSections;
};

const ROW_TYPE_TO_GRADE_SCALE_TYPE = {
  [ROW_ITEM_KEYS.TOTAL]: ["OVERALL_SCORE"],
  [ROW_ITEM_KEYS.AVERAGE]: ["OVERALL_GRADE"],
  [ROW_ITEM_KEYS.GPA]: ["LOCAL_GRADE"],
};

export const getEnabledGradeScaleIds = ({
  itemId,
  rowConfig,
  gradeScaleTypeMap,
}) => {
  const configIds = _.get(rowConfig, "enabledGradeScaleIds");

  if (_.isArray(configIds) && !_.isEmpty(configIds)) {
    return configIds;
  }

  const gradeScaleTypes = _.get(ROW_TYPE_TO_GRADE_SCALE_TYPE, itemId, []);
  return _.flatMap(gradeScaleTypes, type =>
    _.get(gradeScaleTypeMap, [type, "ids"], [])
  );
};

export const toggleAllItemsInSection = ({ items = [], isEnabled }) =>
  _.map(items, item => {
    if (item.isCheckable === false) return item;
    return {
      ...item,
      isEnabled,
      isIndeterminate: false,
      children: item.children
        ? _.map(item.children, child => ({
            ...child,
            isEnabled,
          }))
        : item.children,
    };
  });

export const isRowSectionHeaderChecked = ({ items = [] }) =>
  _.some(items, item => {
    if (_.get(item, "isCheckable", true) === false) {
      return false;
    }

    const children = _.get(item, "children", []);
    if (_.isEmpty(children)) {
      return _.get(item, "isEnabled", false);
    }

    return _.some(children, child =>
      _.get(child, "isCheckable", true) === false
        ? false
        : _.get(child, "isEnabled", false)
    );
  });

export const getNormalizedYearAggregateItemId = itemId => {
  const normalizedItemId = _.toUpper(_.trim(itemId || ""));
  const splitId = _.split(normalizedItemId, "=>");

  if (_.size(splitId) !== 3) {
    return normalizedItemId;
  }

  const [gradeScaleType, , operation] = splitId;
  if (!gradeScaleType || !operation) {
    return normalizedItemId;
  }

  return `${gradeScaleType}=>${operation}`;
};

/**
 * Gets the label value for a configuration item.
 * First checks for a custom value in the current language,
 * then falls back to the localized key translation.
 *
 * @param {Object} params - The parameters object
 * @param {Object} params.label - The label configuration object
 * @param {string} params.language - The current language code (e.g., 'en')
 * @param {Function} params.t - The translation function
 * @returns {string} The resolved label value
 */
const normalizeLabelConfig = label => {
  if (_.isString(label)) {
    return { localizedKey: label };
  }

  if (!_.isObject(label)) {
    return {};
  }

  return label;
};

const getLocalizedLabelParams = ({ normalizedLabel, t }) => {
  const localizedParams = _.get(normalizedLabel, "localizedKeyParams") || [];

  return _.reduce(
    localizedParams,
    (acc, { key, value, isLocalized }) => {
      if (!key) {
        return acc;
      }

      acc[key] = isLocalized ? t(value) : value;
      return acc;
    },
    {}
  );
};

export const getLabelValue = ({ label, language, t }) => {
  const normalizedLabel = normalizeLabelConfig(label);
  const value = _.get(normalizedLabel, ["value", language]);
  if (_.trim(value || "")) return value;

  const defaultValue = _.get(normalizedLabel, "defaultValue");
  if (_.trim(defaultValue || "")) {
    return defaultValue;
  }

  const localizedKey = _.get(normalizedLabel, "localizedKey");
  if (!localizedKey) return "";
  const localizedLabelParams = getLocalizedLabelParams({ normalizedLabel, t });

  const keys = _.split(_.trim(localizedKey), " ");
  return _.join(
    _.map(keys, key => t(key, localizedLabelParams)),
    " "
  );
};

/**
 * Updates the label configuration with a new custom value for the current language.
 * Preserves the existing localizedKey and any other language values.
 *
 * @param {Object} params - The parameters object
 * @param {Object} params.currentLabel - The current label configuration
 * @param {string} params.newValue - The new value to set
 * @param {string} params.language - The current language code (e.g., 'en')
 * @returns {Object} The updated label configuration
 */
export const updateLabelValue = ({ currentLabel, newValue, language }) => {
  const normalizedLabel = normalizeLabelConfig(currentLabel);
  const existingValue = _.get(normalizedLabel, "value", {});
  return {
    ...normalizedLabel,
    value: {
      ...existingValue,
      [language]: newValue,
    },
  };
};

/**
 * Helper to build config object while preserving existing config metadata.
 * The current UI item order is the source of truth for displayOrder.
 * @param {Array} items - List items to process
 * @param {Object} baseConfig - Base configuration to preserve item metadata from
 * @param {Function} getItemKey - Function to extract key from item
 * @param {Function} buildConfigValue - Function to build config value from item
 * @returns {Object} { displayOrder, config }
 */
const buildConfigFromItemsOrder = ({
  items,
  baseConfig = {},
  getItemKey,
  buildConfigValue,
}) => {
  const displayOrder = [];
  const config = {};
  _.forEach(items, item => {
    const key = getItemKey(item);
    if (!key) {
      return;
    }

    displayOrder.push(key);
    config[key] = buildConfigValue(item, _.get(baseConfig, key));
  });

  return { displayOrder, config };
};

/**
 * Processes grading period columns configuration
 * @param {Object} params - Configuration parameters
 * @returns {Object} Grading period columns configuration
 */
const processGradingPeriodColumns = ({
  items,
  configKey,
  gradingPeriodPrefixes,
  baseConfiguration,
  selectedGradeScaleView,
  isDefaultConfiguration,
}) => {
  // Helper to find grading period prefix
  const findGradingPeriodPrefix = itemId =>
    _.find(gradingPeriodPrefixes, prefix =>
      prefix ? _.startsWith(itemId, prefix) : false
    );

  // Handle ACROSS_GRADING_PERIODS view
  if (
    selectedGradeScaleView === GRADE_SCALE_VIEW_TYPES.ACROSS_GRADING_PERIODS
  ) {
    const getGradeScaleId = item => {
      const itemId = _.get(item, "id", "");
      if (!itemId) return null;
      const matchedPrefix = findGradingPeriodPrefix(itemId);
      return matchedPrefix ? _.replace(itemId, matchedPrefix, "") : itemId;
    };

    const validItems = _.filter(items, item => getGradeScaleId(item));
    const { displayOrder, config } = buildConfigFromItemsOrder({
      items: validItems,
      baseConfig: _.get(
        baseConfiguration,
        [configKey, "acrossGradingPeriods", "config"],
        {}
      ),
      getItemKey: getGradeScaleId,
      buildConfigValue: (item, existingConfig) => ({
        ...existingConfig,
        isEnabled: _.get(item, "isEnabled", false),
        label: _.get(item, "label", {}),
      }),
    });

    return {
      ..._.omit(_.get(baseConfiguration, configKey, {}), [
        "displayOrder",
        "config",
      ]),
      viewType: selectedGradeScaleView,
      acrossGradingPeriods: {
        ..._.get(baseConfiguration, [configKey, "acrossGradingPeriods"], {}),
        displayOrder,
        config,
      },
    };
  }

  // Handle GRADING_PERIOD_WISE view
  const itemsByGradingPeriod = _.groupBy(items, item => {
    const itemId = _.get(item, "id", "");
    const matchedPrefix = findGradingPeriodPrefix(itemId);
    return matchedPrefix ? _.replace(matchedPrefix, /_$/, "") : "topLevel";
  });

  const gradingPeriodWise = {};
  let topLevelDisplayOrder = [];
  let topLevelConfig = {};

  // Process grading period items
  _.forEach(
    _.omit(itemsByGradingPeriod, ["topLevel"]),
    (periodItems, gradingPeriodId) => {
      const existingGradingPeriodWise = _.get(baseConfiguration, [
        configKey,
        "gradingPeriodWise",
        gradingPeriodId,
      ]);

      const { displayOrder, config } = buildConfigFromItemsOrder({
        items: periodItems,
        baseConfig: _.get(existingGradingPeriodWise, "config", {}),
        getItemKey: item => {
          const itemId = _.get(item, "id", "");
          const matchedPrefix = findGradingPeriodPrefix(itemId);
          return _.replace(itemId, matchedPrefix, "");
        },
        buildConfigValue: (item, existingConfig) => ({
          ...existingConfig,
          isEnabled: _.get(item, "isEnabled", false),
          label: _.get(item, "label", {}),
        }),
      });

      gradingPeriodWise[gradingPeriodId] = {
        ...existingGradingPeriodWise,
        displayOrder,
        config,
      };
    }
  );

  // Process top level items (for default configuration)
  if (isDefaultConfiguration) {
    const topLevelItems = _.get(itemsByGradingPeriod, "topLevel", []);
    const result = buildConfigFromItemsOrder({
      items: topLevelItems,
      baseConfig: _.get(baseConfiguration, [configKey, "config"], {}),
      getItemKey: item => _.get(item, "id", ""),
      buildConfigValue: (item, existingConfig) => ({
        ...existingConfig,
        isEnabled: _.get(item, "isEnabled", false),
        label: _.get(item, "label", {}),
      }),
    });
    topLevelDisplayOrder = result.displayOrder;
    topLevelConfig = result.config;
  }

  // Build final configuration
  if (_.isEmpty(gradingPeriodWise)) {
    if (!isDefaultConfiguration) {
      return {
        ..._.omit(_.get(baseConfiguration, configKey, {}), [
          "displayOrder",
          "config",
        ]),
        viewType: selectedGradeScaleView,
        gradingPeriodWise: _.get(
          baseConfiguration,
          [configKey, "gradingPeriodWise"],
          {}
        ),
      };
    }
    return {
      displayOrder: topLevelDisplayOrder,
      config: topLevelConfig,
    };
  }

  return {
    ..._.omit(_.get(baseConfiguration, configKey, {}), [
      "displayOrder",
      "config",
    ]),
    viewType: selectedGradeScaleView,
    gradingPeriodWise,
  };
};

/**
 * Processes regular column sections (general, column aggregates, etc.)
 * @param {Object} params - Configuration parameters
 * @returns {Object} Regular section configuration
 */
const processRegularColumnSection = ({
  items,
  configKey,
  baseConfiguration,
}) => {
  const itemIdToConfigId = {
    subject_course: "COURSE_NAME",
    subject_course_id: "COURSE_ID",
    levels: "LEVEL",
    overall_credits: "OVERALL_CREDITS",
  };

  const { displayOrder, config } = buildConfigFromItemsOrder({
    items,
    baseConfig: _.get(baseConfiguration, [configKey, "config"], {}),
    getItemKey: item => {
      const itemId = _.get(item, "id", "");
      return itemIdToConfigId[itemId] || itemId;
    },
    buildConfigValue: (item, existingConfig) => ({
      ...existingConfig,
      isEnabled: _.get(item, "isEnabled", false),
      label: _.get(item, "label", {}),
    }),
  });

  return {
    ..._.get(baseConfiguration, configKey, {}),
    displayOrder,
    config,
  };
};

/**
 * Processes row sections (grading period aggregates, year-level aggregates, etc.)
 * @param {Object} params - Configuration parameters
 * @returns {Object} Row section configuration
 */
const processRowSection = ({ items, configKey, baseConfiguration }) => {
  const displayOrder = [];
  const config = {};

  _.forEach(items, item => {
    const itemId = _.get(item, "id", "");
    displayOrder.push(itemId);

    const children = _.get(item, "children", []);
    const enabledGradeScaleIds = _.compact(
      _.flatMap(children, child => {
        if (!_.get(child, "isEnabled", false)) return [];
        return _.get(child, "gradeScaleIds", []);
      })
    );

    const existingConfig = _.get(baseConfiguration, [
      configKey,
      "config",
      itemId,
    ]);
    config[itemId] = {
      ...existingConfig,
      isEnabled: _.get(item, "isEnabled", false),
      label: _.get(item, "label", {}),
      enabledGradeScaleIds,
    };
  });

  return {
    ..._.get(baseConfiguration, configKey, {}),
    displayOrder,
    config,
  };
};

/**
 * Builds the configuration object from list items.
 * This transforms the UI state back into the configuration format expected by the backend.
 *
 * @param {Object} params - The parameters object
 * @param {Object} params.listItems - Map of sectionId to items array
 * @param {Array} params.columnSections - Column section configurations
 * @param {Array} params.rowSections - Row section configurations
 * @returns {Object} The configuration object
 */
export const buildConfigurationFromListItems = ({
  listItems,
  columnSections,
  rowSections,
  gradingPeriods = [],
  baseConfiguration = {},
  selectedGradeScaleView = GRADE_SCALE_VIEW_TYPES.GRADING_PERIOD_WISE,
  selectedGradeId = null,
}) => {
  const configuration = _.omit(_.cloneDeep(baseConfiguration), [
    "isOverridden",
  ]);
  const gradingPeriodPrefixes = _.map(gradingPeriods, ({ gradingPeriod }) => {
    const gradingPeriodId = _.get(gradingPeriod, "id");
    return gradingPeriodId ? `${gradingPeriodId}_` : "";
  });
  const isDefaultConfiguration = !selectedGradeId;

  // Section ID to config key mapping
  const COLUMN_SECTION_TO_CONFIG_KEY = {
    general_columns: "general",
    grading_period_columns: "gradeScales",
    grading_period_aggregates_for_row: "columnAggregates",
  };

  const ROW_SECTION_TO_CONFIG_KEY = {
    grading_period_aggregates: "rowAggregates",
    year_level_aggregates: "yearAggregates",
    across_year_aggregates: "acrossYearAggregates",
  };

  // Process column sections
  _.forEach(columnSections, section => {
    const sectionId = _.get(section, "sectionId");
    const configKey = COLUMN_SECTION_TO_CONFIG_KEY[sectionId];
    if (!configKey) return;

    const items = _.get(listItems, sectionId, []);

    if (sectionId === "grading_period_columns") {
      configuration[configKey] = processGradingPeriodColumns({
        items,
        configKey,
        gradingPeriodPrefixes,
        baseConfiguration,
        selectedGradeScaleView,
        isDefaultConfiguration,
      });
    } else {
      configuration[configKey] = processRegularColumnSection({
        items,
        configKey,
        baseConfiguration,
      });
    }
  });

  // Process row sections
  _.forEach(rowSections, section => {
    const sectionId = _.get(section, "sectionId");
    const configKey = ROW_SECTION_TO_CONFIG_KEY[sectionId];
    if (!configKey) return;

    const items = _.get(listItems, sectionId, []);
    configuration[configKey] = processRowSection({
      items,
      configKey,
      baseConfiguration,
    });
  });

  return configuration;
};
